import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { scrollToHash } from 'lib/window/scrollUtils'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import { fetchFaq } from 'actions/ContentActions'
import usePrevious from 'hooks/usePrevious'
import { rem } from 'polished'

import MarkdownRender from 'LuxKit/MarkdownRender'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import SmallToggleButton from 'LuxKit/Button/SmallToggleButton'
import Heading from 'LuxKit/Typography/Heading'
import useToggle from 'hooks/useToggle'
import FaqStructuredData from 'components/Content/FrequentlyAskedQuestions/FaqStructuredData'
import Accordion from 'LuxKit/Accordion'
import VerticalSpacer from '../Spacing/VerticalSpacer'
import { useIntl } from 'react-intl'

function itemMatchesAnyTags(item: App.LegalFaqPageContent, tags: Array<string>) {
  return !!item.tags?.some(tag => tags.includes(tag))
}

const SwitchButton = styled(SmallToggleButton)`
  margin: ${rem(16)} auto;
  display: block;
`

const StyledTitle = styled(Heading)`
  margin-top: ${rem(24)};
`

interface Props {
  slug: string
  numbered?: boolean
  maxToShow?: number
  tags?: Array<string>
  wrapper?: ({ children }: { children: React.ReactNode }) => React.ReactElement
  type?: 'legal' | 'destinationProduct'
  faqs: App.ContentState['faqs']
  hash?: string
}

function Faq({
  numbered,
  maxToShow,
  tags,
  wrapper,
  faqs,
  type = 'legal',
  slug,
  hash,
}: Props) {
  const intl = useIntl()
  const prevFaqs = usePrevious(faqs, { legal: {}, destinationProduct: {} })
  const content: App.DestinationProductFaqPageContent | Array<App.LegalFaqPageContent> | null | undefined =
    faqs[type][slug]?.content === null ? null : faqs[type][slug]?.content?.pageContent
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (content === undefined) {
      dispatch(fetchFaq(type, slug))
    } else if (content && hash && prevFaqs[type][slug]?.fetching !== faqs[type][slug]?.fetching) {
      setTimeout(() => scrollToHash(hash), 1000)
    }
  })

  let filteredContent = content

  if (tags?.length) {
    filteredContent = content?.filter(item => itemMatchesAnyTags(item, tags))
  }

  const Wrapper = (filteredContent?.length && wrapper) ? wrapper : 'div'

  const { value: expanded, toggle: toggleExpand } = useToggle()

  const getLabel = (title: string, i: number) => numbered ? `${i}. ${title}` : title

  const contents = useMemo(() => ({
    visible: filteredContent?.slice(0, maxToShow) ?? [],
    hiddenContent: filteredContent?.slice(maxToShow) ?? [],
  }), [filteredContent, maxToShow])

  // using index var i to make sure the collapsible has the right order label
  let i = 1

  const loading = !!faqs[type][slug]?.fetching

  return (
    <Wrapper>
      {!!contents.visible.length && (
        <FaqStructuredData
          items={contents.visible.map(item => ({
            question: item.title,
            answer: item.answer ?? '',
          }))}
        />
      )}
      {loading && <LoadingIndicator inline />}

      {contents.visible.map((faq) => {
        if (faq.contentTypeId === 'legalTermAndCondition' || faq.contentTypeId === 'componentContent') {
          return (
            <VerticalSpacer gap={24} key={faq.slug} id={faq.slug}>
              <StyledTitle variant="heading3" format="uppercase" forwardedAs="h2">
                {faq.title}
              </StyledTitle>
              <MarkdownRender content={faq.description ?? ''} />
            </VerticalSpacer>
          )
        } else {
          return (
            <Accordion
              key={faq.slug}
              id={faq.slug}
              defaultOpen={(hash === `#${faq.slug}`)}
              heading={getLabel(faq.title, i++)}
            >
              <MarkdownRender content={faq.answer ?? ''} />
            </Accordion>
          )
        }
      })}

      {maxToShow && contents.hiddenContent.length > 0 &&
        <>
          {!expanded && <SwitchButton on={expanded} onClick={toggleExpand} data-testid="show-all-faqs">
            {intl.formatMessage({
              id: 'PQzh_MJ-k_kOTZ2sz0uS',
              defaultMessage: 'Show All FAQs',
              description: 'Toggle button to reveal all frequently asked questions',
            })}
          </SwitchButton>}
          {expanded && contents.hiddenContent.map((faq) => (
            <Accordion
              key={faq.slug}
              heading={getLabel(faq.title, i++)}
              id={faq.slug}
              expanded={(hash === `#${faq.slug}`)}
            >
              <MarkdownRender content={faq.answer ?? ''} />
            </Accordion>
          ))}

        </>}
    </Wrapper>
  )
}

function mapStateToProps(state: App.State) {
  return ({
    faqs: state.content.faqs,
    hash: state.router.location.hash,
  })
}

export default connect(mapStateToProps)(Faq)
